import React, { useState } from 'react';
import {
  Button, CircularProgress, Alert, Snackbar, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemIcon, ListItemText
} from '@mui/material';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useNavigate } from 'react-router-dom';
import { createCall } from '../../services/callsApi';
import api from '../../services/api';

export default function StartCallButton({ token, clientId, conversationId, variant = 'contained', sx }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Spec H — soft gate: when required pre-session tasks are still open we
  // surface a confirm dialog with a 1-click override.
  const [gateDialog, setGateDialog] = useState(null); // { incompleteTasks: [...] }

  async function checkPreSessionGate() {
    try {
      const res = await api.get(`/pre-session/assignments/client/${clientId}`, token);
      const assignments = res?.data?.assignments || [];
      const open = assignments.filter(a => a.status !== 'completed' && a.status !== 'cancelled');
      const incomplete = [];
      for (const a of open) {
        const completionsById = new Map((a.taskCompletions || []).map(c => [String(c.taskId), c]));
        const tasks = a.templateSnapshot?.tasks || [];
        for (const t of tasks) {
          if (t.required === false) continue;
          const c = completionsById.get(String(t._id));
          if (c?.status !== 'completed') {
            incomplete.push({ title: t.title, type: t.type, assignmentName: a.templateSnapshot?.name });
          }
        }
      }
      return incomplete;
    } catch (err) {
      // Non-fatal — pre-session may not be enabled, or endpoint missing.
      // Don't block the call on a gate-check failure.
      console.warn('[StartCallButton] pre-session gate check failed:', err?.message);
      return [];
    }
  }

  async function startCallNow() {
    setLoading(true);
    setGateDialog(null);
    try {
      const { liveCallId, token: livekitToken } = await createCall({ token, clientId, conversationId });
      navigate(`/call/${liveCallId}`, { state: { livekitToken } });
    } catch (err) {
      console.error('[StartCallButton] createCall error:', err);
      const msg = err?.response?.data?.msg || 'Could not start call';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  const handleClick = async () => {
    if (!clientId) {
      setError('Missing client reference');
      return;
    }
    setLoading(true);
    const incomplete = await checkPreSessionGate();
    setLoading(false);
    if (incomplete.length > 0) {
      setGateDialog({ incompleteTasks: incomplete });
      return;
    }
    await startCallNow();
  };

  return (
    <>
      <Button
        variant={variant}
        startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <VideoCallIcon />}
        onClick={handleClick}
        disabled={loading}
        sx={sx}
      >
        {loading ? 'Starting…' : 'Start video call'}
      </Button>
      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      {/* Soft-gate dialog — incomplete required pre-session tasks */}
      <Dialog open={!!gateDialog} onClose={() => setGateDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <WarningAmberIcon color="warning" />
            <span>Pre-session tasks incomplete</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            The client hasn&apos;t completed {gateDialog?.incompleteTasks?.length}{' '}
            required pre-session task{gateDialog?.incompleteTasks?.length === 1 ? '' : 's'}.
            You can still start the call.
          </Alert>
          <List dense>
            {(gateDialog?.incompleteTasks || []).map((t, i) => (
              <ListItem key={i} disableGutters>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <RadioButtonUncheckedIcon fontSize="small" color="action" />
                </ListItemIcon>
                <ListItemText primary={t.title} secondary={t.assignmentName} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGateDialog(null)}>Wait</Button>
          <Button variant="contained" color="warning" onClick={startCallNow}>
            Start anyway
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
